import React, { SetStateAction, useEffect, useState } from "react";
import { useTreeMapFilterState } from "src/store/features/MarketTreeMapFilter.slice";
import MarketTreeMapSlider from "./MarketTreeMapSlider";
import MarketTreeMapFilter from "./MarketTreeMapFilter";
import CloseIcon from "@mui/icons-material/Close";
import ReplayIcon from "@mui/icons-material/Replay";
import Chip from "@mui/material/Chip";
import MarketToolForm from "./MarketTreeMapForm";
import {
  Stack,
  Tooltip,
  Divider,
  SwipeableDrawer,
  Typography,
  Switch,
  useTheme,
} from "@mui/material";
import { setFilterValue } from "src/store/features/MarketTreeMapFilter.slice";
import {
  toggleTimeFrame,
  setTreemapCoins,
  hideCoin,
  setMinMaxValue,
} from "src/store/features/MarketTreeMapFilter.slice";
import { TreeMapData } from "../types";
import { useAppDispatch } from "src/store/store";
import { MarketTreeMapStyle } from "./MarketTreeMap.styles";
import MarketTreeMapInfo from "./MarketTreeMapInfo";
import { trackEvent } from "src/Amplitude";

interface MarketTreeMapToolBarProps {
  data?: TreeMapData[];
  roaming: boolean;
  roamingSet: SetStateAction<any>;
  sectorSplitSet: SetStateAction<any>;
  sectorSplit: boolean;
  drawerOpen: boolean;
  setsDrawerOpen: any;
}

const MarketTreeMapToolBar = (props: MarketTreeMapToolBarProps) => {
  const [sortedData, setSortedData] = useState<number[] | undefined>();
  const { data, roaming, roamingSet, sectorSplitSet, sectorSplit, drawerOpen, setsDrawerOpen } =
    props;
  const toolTipReset = "Reset chart";
  const toolTipZoom = "Enable the click to zoom. Use bottom navigation to zoom out of sub sector. ";

  // ------------------ Styles---------------------------

  const classes = MarketTreeMapStyle();
  const theme = useTheme();
  // ------------------ Redux---------------------------
  const dispatch = useAppDispatch();
  const { hideCoins } = useTreeMapFilterState();
  // ------------------ Constants---------------------------
  const anchor = "right";

  const timeFormTitle = "Performance";
  const filterOptionsDesc =
    "Remove items from the sector map. Items can be added back by resetting the chart or clicking the removed item below.";

  const timeFrameDesc = `Filter the range of cryptocurrency's by the ${performance} size `;
  const subSectionDesc = "Disable all coins primary sub sectors";
  const timeFilter = [
    { id: 1, option: "Daily" },
    { id: 2, option: `Weekly` },
    { id: 3, option: `Monthly` },
  ];

  // ------------------Functions---------------------------
  const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event &&
      event.type === "keydown" &&
      ((event as React.KeyboardEvent).key === "Tab" ||
        (event as React.KeyboardEvent).key === "Shift")
    ) {
    }
  };

  // Removes individual coins when @ mui chip item is selected
  const removeItem = (index: number) => {
    dispatch(hideCoin([...hideCoins.slice(0, index), ...hideCoins.slice(index + 1)]));
  };
  // ------------------Effects---------------------------

  // Fetch the min and max market cap and add to redux for the filter options
  useEffect(() => {
    const result = data
      ?.map((x) => x.children)
      .flat()
      .map((y) => y.name);

    if (sortedData && sortedData?.length > 0) {
      const maxNumber = sortedData.reduce((a, b) => Math.max(a, b), -Infinity);
      const minNumber = sortedData.reduce((a, b) => Math.min(a, b));

      dispatch(setMinMaxValue({ min: minNumber, max: maxNumber }));
    }

    if (result) {
      dispatch(setTreemapCoins(result));
    }
  }, [data, performance, sectorSplit, sortedData]);

  // Fetch the data for the drop down dependant on weather the sector split is selected or not as it return different data sets
  useEffect(() => {
    if (!sectorSplit) {
      const resultNestedValue = data
        ?.map((x) => x.children)
        .flat()
        ?.map((x: any) => x.children)
        .flat()
        .map((y) => y.value)
        .filter((el) => el != null);

      setSortedData(resultNestedValue);
    }

    if (sectorSplit) {
      const resultValue = data
        ?.map((x) => x.children)
        .flat()
        .map((y) => y.value)
        .filter((el) => el != null);
      setSortedData(resultValue);
    }
  }, [data]);

  return (
    <SwipeableDrawer
      anchor={anchor}
      className={classes.drawer}
      open={drawerOpen}
      variant="persistent"
      PaperProps={{
        sx: {
          backgroundColor: "#fff",
          width: "250px",
        },
      }}
      onClose={toggleDrawer(false)}
      onOpen={toggleDrawer(true)}>
      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        spacing={2}
        sx={{ height: "68px" }}>
        <Typography className={classes.drawerTitle}>Options</Typography>
      </Stack>
      <Divider sx={{ marginBottom: "20px" }} light />
      <Stack
        direction={"column"}
        justifyContent="flex-start"
        alignItems="flex-start"
        spacing={3}
        mr={4}
        ml={4}
        mb={{ xs: 1, sm: 1, md: 4 }}>
        <Stack direction="column" sx={{ width: "100%" }}>
          <MarketToolForm toggleRedux={toggleTimeFrame} title={timeFormTitle} values={timeFilter} />
        </Stack>

        <Stack direction="column" sx={{ width: "100%" }}>
          <MarketTreeMapSlider />
          <MarketTreeMapInfo info={timeFrameDesc} />
        </Stack>
        <Stack direction="column" sx={{ width: "100%" }}>
          <MarketTreeMapFilter />
          <MarketTreeMapInfo info={filterOptionsDesc} />
        </Stack>
        <Stack direction="column" justifyContent="flex-start" alignItems="center" spacing={1}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
            sx={{ width: "100%", height: "30px" }}>
            <Typography>Zoom Mode</Typography>
            <Tooltip title={toolTipZoom} placement="top-end">
              <Switch
                checked={roaming}
                onChange={() => roamingSet(!roaming)}
                onClick={() =>
                  trackEvent(`Select_Market_Sectors`, {
                    option: "Advanced_Options_Zoom_Mode",
                  })
                }
              />
            </Tooltip>
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
            sx={{ width: "100%", height: "30px" }}>
            <Typography>Sub Sectors</Typography>
            <Tooltip title={subSectionDesc} placement="top-end">
              <Switch
                onClick={() =>
                  trackEvent(`Select_Market_Sectors`, {
                    option: "Advanced_Options_Sub_Sectors",
                  })
                }
                checked={sectorSplit}
                onChange={() => sectorSplitSet(!sectorSplit)}
              />
            </Tooltip>
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
            onClick={() => {
              dispatch(hideCoin([]));
              dispatch(setFilterValue([0, 500000000000]));
              sectorSplitSet(false);
              roamingSet(false);
              trackEvent("Select_Sectors", { option: "Advanced_Options_Reset" }, "Markets");
            }}
            sx={{
              width: "100%",
              height: "30px",
              cursor: "pointer",
              "&:hover": { color: theme.palette.secondary.main },
            }}>
            <Typography>Reset </Typography>
            <Tooltip title={toolTipReset} placement="top-end">
              <ReplayIcon
                sx={{
                  height: "25px",
                  marginRight: "14px  !important",
                }}
              />
            </Tooltip>
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
            onClick={() => {
              setsDrawerOpen(false);
              trackEvent("Select_Sectors_Advanced", { option: "Options_Close_Options" }, "Markets");
            }}
            sx={{
              width: "100%",
              height: "30px",
              cursor: "pointer",
              "&:hover": { color: theme.palette.secondary.main },
            }}>
            <Typography>Close Options</Typography>
            <CloseIcon
              sx={{
                height: "25px",
                marginRight: "14px  !important",
              }}
              className={classes.drawToggle}
            />
          </Stack>
        </Stack>
      </Stack>
      <Divider sx={{ marginBottom: "10px" }} light />
      <Stack direction="row" justifyContent="center" sx={{ flexWrap: "wrap" }} alignItems="center">
        {hideCoins?.map((value: any) => (
          <Chip
            onClick={() => {
              let index = hideCoins.findIndex((x: any) => x === value);
              removeItem(index);
            }}
            className={classes.chipHide}
            key={value}
            label={value}
          />
        ))}
      </Stack>
    </SwipeableDrawer>
  );
};

export default MarketTreeMapToolBar;
